import { useState, useEffect, useCallback, useRef } from 'react';
import { CastMember, PipelineRun } from '../api/structs';
import PipelineAPI, { ContinuePipelineOptions, RegenerateScope } from '../api/pipeline';
import { MediaAttachment, PipelineInputAttachment } from '../api/structs/pipeline';
import { isRateLimited } from '../api/rateLimitPause';
import { subscribeRunLifecycle } from './runLifecycleEvents';

const STORAGE_KEY = 'active_pipeline_runs';
const COMPLETED_STATE = 'completed';
const FAILED_STATE = 'failed';
const CANCELLED_STATE = 'cancelled';
const TERMINAL_STATES = [COMPLETED_STATE, CANCELLED_STATE];
const AUTO_REMOVE_STATES = [CANCELLED_STATE];
// A director run waits for its director (the chat) between steps: that is where it
// spends most of its life, and it is not finished.
const SERVER_SYNC_STATES = ['pending', 'running', 'paused', 'awaiting_director', COMPLETED_STATE, FAILED_STATE];
// Single adaptive poll: 3s while any tracked run is still non-terminal, 5s once
// everything has settled. Replaces the old dual-loop (5s list sync + a 2s
// per-run detail poll) — GET /api/pipelines returns the same full PipelineRun
// payload (including per-checkpoint results) as GET /api/pipelines/:id, so the
// list sync alone is already complete; no follow-up detail fetch is needed.
const SERVER_SYNC_INTERVAL_ACTIVE_MS = 3000;
const SERVER_SYNC_INTERVAL_IDLE_MS = 5000;

interface StoredRun {
  id: string;
  templateId: string;
  initialInput: string;
  startedAt: string;
}

// Every poll tick upserts every run, and each upsert checks the remembered
// list, so the parse is memoised on the raw string. Reads still go to storage
// each time — another hook instance or tab may have written — and only skip the
// parse when the text is unchanged. The array is shared: never mutate it.
let storedRunsMemo: { raw: string; runs: readonly StoredRun[] } | null = null;

const readStoredRuns = (): readonly StoredRun[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    if (storedRunsMemo?.raw === raw) return storedRunsMemo.runs;
    const parsed: unknown = JSON.parse(raw);
    const runs: readonly StoredRun[] = Array.isArray(parsed) ? parsed : [];
    storedRunsMemo = { raw, runs };
    return runs;
  } catch (err) {
    console.error('Failed to load stored runs:', err);
    return [];
  }
};

const writeStoredRuns = (runs: readonly StoredRun[]) => {
  const raw = JSON.stringify(runs);
  localStorage.setItem(STORAGE_KEY, raw);
  storedRunsMemo = { raw, runs };
};

const isChildRun = (run: Pick<PipelineRun, 'parent_run_id'>) => Boolean(run.parent_run_id);
// The list endpoint already leaves archived runs out; a detail read (restore,
// socket refetch) does not, and an archived run must never re-enter the rail.
const isArchivedRun = (run: Pick<PipelineRun, 'archived_at'>) => Boolean(run.archived_at);

export function usePipelineRuns() {
  const [runs, setRuns] = useState<Map<string, PipelineRun>>(new Map());
  // Runs this client has deleted or archived. A list or detail response that
  // was already in flight when the DELETE/archive landed would otherwise put the
  // run straight back into the map — and nothing ever prunes it again, so a
  // thread the user just turned into a clip stays on screen.
  const deletedRunIds = useRef<Set<string>>(new Set());

  // Storage is the one copy of the remembered list: read, change, write back.
  // (It used to be mirrored in state too, and the restore effect keyed on that
  // mirror — so every run started or finished re-ran the whole restore.)
  const saveStoredRun = useCallback((run: StoredRun) => {
    const prev = readStoredRuns();
    const existing = prev.find((item) => item.id === run.id);
    if (
      existing &&
      existing.templateId === run.templateId &&
      existing.initialInput === run.initialInput &&
      existing.startedAt === run.startedAt
    ) {
      return;
    }
    writeStoredRuns([...prev.filter((item) => item.id !== run.id), run]);
  }, []);

  const removeStoredRun = useCallback((runId: string) => {
    const prev = readStoredRuns();
    const updated = prev.filter((item) => item.id !== runId);
    if (updated.length === prev.length) {
      return;
    }
    writeStoredRuns(updated);
  }, []);

  const shouldKeepTracked = useCallback((status: string) => !AUTO_REMOVE_STATES.includes(status), []);

  // Forget a run locally, without calling the server.
  const dropRun = useCallback(
    (runId: string) => {
      removeStoredRun(runId);
      setRuns((prev) => {
        if (!prev.has(runId)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(runId);
        return next;
      });
    },
    [removeStoredRun]
  );

  const upsertRun = useCallback(
    (run: PipelineRun, fallback?: Pick<StoredRun, 'templateId' | 'initialInput' | 'startedAt'>) => {
      if (deletedRunIds.current.has(run.id)) {
        return;
      }
      if (isChildRun(run) || isArchivedRun(run)) {
        removeStoredRun(run.id);
        setRuns((prev) => {
          const next = new Map(prev);
          next.delete(run.id);
          return next;
        });
        return;
      }

      setRuns((prev) => new Map(prev).set(run.id, run));
      if (shouldKeepTracked(run.status)) {
        saveStoredRun({
          id: run.id,
          templateId: fallback?.templateId ?? run.pipeline_template_id,
          initialInput: fallback?.initialInput ?? run.initial_input,
          startedAt: fallback?.startedAt ?? run.created_at ?? new Date().toISOString(),
        });
      } else {
        removeStoredRun(run.id);
      }
    },
    [removeStoredRun, saveStoredRun, shouldKeepTracked]
  );

  // Settles one remembered run against what the server says about it: tracked
  // again, or forgotten.
  const settleRememberedRun = useCallback(
    (run: PipelineRun | undefined, stored: StoredRun) => {
      if (run && shouldKeepTracked(run.status) && !isChildRun(run) && !isArchivedRun(run)) {
        upsertRun(run, stored);
      } else {
        dropRun(stored.id);
      }
    },
    [dropRun, shouldKeepTracked, upsertRun]
  );

  // Restore the runs remembered from the last load. The run list this load has
  // just fetched already holds them in full (the same payload as the by-id
  // read), so only a remembered run the list does NOT hold — archived, a
  // fan-out child, deleted — costs a request, one at a time. Called once per
  // load, with the first list that arrives.
  //
  // A run is forgotten when the server says it is gone (404) or no longer
  // worth tracking. A 429 or a network error says nothing about the run: it
  // stays remembered, and while a 429 pause is running no further by-id
  // request is made.
  const restoreRememberedRuns = useCallback(
    async (listed: PipelineRun[], isCancelled: () => boolean) => {
      const listedById = new Map(listed.map((run) => [run.id, run]));
      for (const stored of readStoredRuns()) {
        if (isCancelled()) return;
        const listedRun = listedById.get(stored.id);
        if (listedRun) {
          settleRememberedRun(listedRun, stored);
          continue;
        }
        // Settling from the list costs nothing; only the by-id fetch waits out a
        // pause. Those runs stay remembered for the next load.
        if (isRateLimited()) continue;
        try {
          const run = await PipelineAPI.getPipeline(stored.id);
          if (isCancelled()) return;
          settleRememberedRun(run, stored);
        } catch (err) {
          if (isCancelled()) return;
          console.error(`Failed to restore run ${stored.id}:`, err);
          if ((err as { response?: { status?: number } })?.response?.status === 404) {
            // Out of the map too, not just storage — otherwise it lingers as an
            // unbacked thread.
            dropRun(stored.id);
          }
        }
      }
    },
    [dropRun, settleRememberedRun]
  );

  // Single adaptive poll of the run list. Cadence speeds up to 3s while any
  // tracked run is still in flight and relaxes to 5s once everything is
  // terminal. Self-scheduling via setTimeout (rather than setInterval keyed
  // off `runs`) so the loop's own timing is never torn down/recreated by the
  // state updates it causes.
  const hasActiveRunsRef = useRef(false);
  const restoreStartedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const scheduleNext = () => {
      if (cancelled) return;
      const delay = hasActiveRunsRef.current
        ? SERVER_SYNC_INTERVAL_ACTIVE_MS
        : SERVER_SYNC_INTERVAL_IDLE_MS;
      timeoutId = setTimeout(runSync, delay);
    };

    const runSync = async () => {
      // The API asked to be left alone (429): skip this tick, keep the schedule.
      if (isRateLimited()) {
        scheduleNext();
        return;
      }
      try {
        const serverRuns = await PipelineAPI.listPipelines();
        const relevantRuns = serverRuns.filter(
          (run) =>
            SERVER_SYNC_STATES.includes(run.status) && !isChildRun(run) && !isArchivedRun(run)
        );
        relevantRuns.forEach((run) => upsertRun(run));
        hasActiveRunsRef.current = relevantRuns.some(
          (run) => !TERMINAL_STATES.includes(run.status)
        );
        // Not started by a response that outlived its effect (StrictMode's
        // throwaway first mount): the live effect's own response starts it.
        if (!cancelled && !restoreStartedRef.current) {
          restoreStartedRef.current = true;
          void restoreRememberedRuns(serverRuns, () => cancelled);
        }
      } catch (err) {
        console.error('Failed to sync pipeline runs from server:', err);
        // Leave hasActiveRunsRef at its last known value so a transient
        // failure doesn't relax the cadence while runs are still active.
      } finally {
        scheduleNext();
      }
    };

    void runSync();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [restoreRememberedRuns, upsertRun]);

  const startRun = useCallback(
    async (
      templateId: string,
      input: string,
      options: {
        autoMode: boolean;
        initialAttachments?: PipelineInputAttachment[];
        musicMediaId?: string | null;
        parameters?: Record<string, unknown>;
        /** Library "Run again": attribute the run to an existing idea. */
        ideaId?: string;
      }
    ): Promise<PipelineRun> => {
      const response = await PipelineAPI.startPipeline(templateId, input, options);
      const fullRun = await PipelineAPI.getPipeline(response.run_id);

      upsertRun(fullRun, {
        templateId,
        initialInput: input,
        startedAt: new Date().toISOString(),
      });

      return fullRun;
    },
    [upsertRun]
  );

  /**
   * Registers a run this client learned about from OUTSIDE the store — a series
   * episode the backend just started, whose id arrived on the start response
   * rather than through this hook's own `startRun`. One fetch, then the run
   * lives here like any other: the same list poll, the same socket refetch, the
   * same localStorage restore after a reload (upsertRun persists it).
   *
   * Additive on purpose. A second poller for externally-owned runs is exactly
   * what this method exists to prevent.
   */
  const trackRun = useCallback(
    async (runId: string): Promise<PipelineRun | null> => {
      if (!runId) return null;
      try {
        const run = await PipelineAPI.getPipeline(runId);
        upsertRun(run);
        return run;
      } catch (err) {
        console.error(`Failed to track run ${runId}:`, err);
        return null;
      }
    },
    [upsertRun]
  );

  // A lifecycle event refetches the run it names, so a completion reaches the
  // UI on the socket rather than on the next poll tick. Scoped to runs already
  // tracked here: a fan-out child's events (and every other user's run the hub
  // might echo) are not this store's business.
  const trackedIdsRef = useRef<Set<string>>(new Set());
  // Mirrored at COMMIT time, never in the render body: the subscriber reads the
  // ref lazily when an event lands, so a committed snapshot is soon enough —
  // and a render-phase write is the pattern React documents as unsafe.
  useEffect(() => {
    trackedIdsRef.current = new Set(runs.keys());
  }, [runs]);

  useEffect(
    () =>
      subscribeRunLifecycle(({ runId }) => {
        if (!trackedIdsRef.current.has(runId) || deletedRunIds.current.has(runId)) return;
        PipelineAPI.getPipeline(runId)
          .then((run) => upsertRun(run))
          .catch(() => {
            // The poll is still the backstop; a missed refetch costs latency,
            // never correctness.
          });
      }),
    [upsertRun]
  );

  const continueRun = useCallback(
    async (runId: string, outputOverride?: string, opts?: ContinuePipelineOptions) => {
      await PipelineAPI.continuePipeline(runId, outputOverride, opts);
      const updated = await PipelineAPI.getPipeline(runId);
      upsertRun(updated);
    },
    [upsertRun]
  );

  const regenerateCheckpoint = useCallback(
    async (runId: string, checkpoint: number, promptOverride?: string, scope?: RegenerateScope) => {
      await PipelineAPI.regenerateCheckpoint(runId, checkpoint, promptOverride, scope);
      const updated = await PipelineAPI.getPipeline(runId);
      upsertRun(updated);
    },
    [upsertRun]
  );

  /** Pins which generation of a checkpoint downstream steps consume ('' clears). */
  const selectCheckpointOutput = useCallback(
    async (runId: string, checkpoint: number, attachmentId: string) => {
      await PipelineAPI.selectCheckpointOutput(runId, checkpoint, attachmentId);
      const updated = await PipelineAPI.getPipeline(runId);
      upsertRun(updated);
    },
    [upsertRun]
  );

  const injectCheckpointPrompt = useCallback(
    async (
      runId: string,
      checkpointIndex: number,
      text: string,
      options?: {
        autoRegenerate?: boolean;
        source?: string;
      }
    ) => {
      await PipelineAPI.injectCheckpointPrompt(runId, checkpointIndex, text, options);
      const updated = await PipelineAPI.getPipeline(runId);
      upsertRun(updated);
      return updated;
    },
    [upsertRun]
  );

  // Cancel and remove are the same server call — DELETE /api/pipelines/:id is a
  // hard delete in every status — and must stay the same client operation.
  const deleteRun = useCallback(async (runId: string) => {
    // Tombstoned BEFORE the await, which is the whole point: the delete cascade
    // releases a credit reservation per fan-out child, so it routinely outlasts
    // a poll interval and a list response lands after the local removal.
    deletedRunIds.current.add(runId);
    try {
      await PipelineAPI.deletePipeline(runId);
    } catch (err) {
      deletedRunIds.current.delete(runId);
      throw err;
    }
    removeStoredRun(runId);
    setRuns((prev) => {
      const next = new Map(prev);
      next.delete(runId);
      return next;
    });
  }, [removeStoredRun]);

  const cancelRun = deleteRun;
  const removeRun = deleteRun;

  /**
   * Generate Clip's exit: the thread leaves the rail exactly as a delete would,
   * but the server keeps the run so its quality report stays exportable from
   * the clip. Never falls back to a delete — a failed archive leaves the run
   * on the server (it reappears after a reload) and rethrows for the caller to
   * report; locally it is gone either way.
   */
  const archiveRun = useCallback(async (runId: string) => {
    deletedRunIds.current.add(runId);
    try {
      await PipelineAPI.archivePipeline(runId);
    } finally {
      dropRun(runId);
    }
  }, [dropRun]);

  const addCheckpointAttachment = useCallback(
    async (
      runId: string,
      checkpointIndex: number,
      attachment: Omit<MediaAttachment, 'id' | 'created_at'>
    ) => {
      await PipelineAPI.addAttachment(runId, checkpointIndex, attachment);
      const updated = await PipelineAPI.getPipeline(runId);
      upsertRun(updated);
      return updated;
    },
    [upsertRun]
  );

  // Expose updateRun for components that need to update a single run after refetch
  const updateRun = useCallback((run: PipelineRun) => {
    upsertRun(run);
  }, [upsertRun]);

  /**
   * A cast write, and nothing else. Every other writer hands over a run it has
   * just FETCHED; a cast write answers with the cast alone, so a run rebuilt
   * around it locally is as old as the copy the caller happened to hold — and a
   * poll or a WebSocket update landing in the same tick would be reverted.
   *
   * Merges functionally into whatever the map holds at the moment of the write.
   * A run the store does not have is nothing to merge into: it is not this
   * hook's job to conjure one (an untracked run has no template, no stored
   * entry, and would never be polled).
   */
  const updateRunCast = useCallback((runId: string, cast: CastMember[]) => {
    setRuns((prev) => {
      const current = prev.get(runId);
      if (!current) return prev;
      return new Map(prev).set(runId, { ...current, cast });
    });
  }, []);

  return {
    runs: Array.from(runs.values()),
    startRun,
    trackRun,
    continueRun,
    regenerateCheckpoint,
    selectCheckpointOutput,
    injectCheckpointPrompt,
    addCheckpointAttachment,
    cancelRun,
    removeRun,
    archiveRun,
    updateRun,
    updateRunCast,
  };
}
